import { Api } from 'utils';
import { axios } from 'utils';
import { store } from 'store';

// ---------- Tipos de request ----------

export interface CronLogsParams {
	job_name?: string;
	status?: string;
	from?: string;
	to?: string;
	page?: number;
	limit?: number;
	withoutLoading?: boolean;
}

export interface Pm2LogsParams {
	file?: string;
	search?: string;
	limit?: number;
	page?: number;
	date_from?: string;
	date_to?: string;
	withoutLoading?: boolean;
}

// ---------- Tipos de response (shapes definidos según el spec del backend) ----------

export interface CronJobStatus {
	job_name: string;
	enabled?: boolean;
	last_run_at?: string | null;
	last_status?: string | null;
	next_run_at?: string | null;
	schedule?: string | null;
}

export interface CronStatusResponse {
	jobs?: CronJobStatus[];
	config?: Record<string, string>;
}

export interface CronLogRow {
	id?: number;
	job_name?: string;
	cycle_id?: string | null;
	status?: string;
	started_at?: string;
	finished_at?: string | null;
	duration_ms?: number | null;
	total_processed?: number | null;
	successful_updates?: number | null;
	failed_updates?: number | null;
	error_message?: string | null;
	metadata?: any;
	created_at?: string;
	updated_at?: string;
}

export interface CronLogsResponse {
	logs?: CronLogRow[];
	pagination?: {
		page?: number;
		limit?: number;
		total?: number;
		total_pages?: number;
	};
	filters_applied?: {
		job_name?: string | null;
		status?: string | null;
		from?: string | null;
		to?: string | null;
	};
}

export interface UpdateConfigResponse {
	success?: boolean;
	key_name?: string;
	value?: string;
}

export interface RunNowResponse {
	success?: boolean;
	job_name?: string;
	queued?: boolean;
	started_at?: string;
}

export interface HealthResponse {
	status?: 'ok' | 'degraded' | 'down' | string;
	uptime_seconds?: number;
	db?: { connected?: boolean; latency_ms?: number };
	memory?: { used_mb?: number; total_mb?: number };
	cpu?: { load_avg?: number[] };
	[key: string]: any;
}

export interface Pm2LogRow {
	timestamp?: string;
	level?: string;
	message?: string;
	file?: string;
}

export interface Pm2LogsResponse {
	rows?: Pm2LogRow[];
	count?: number;
	page?: number;
	limit?: number;
	files?: string[];
}

export interface BackupRow {
	filename: string;
	size_bytes?: number;
	created_at?: string;
}

export interface BackupsResponse {
	backups?: BackupRow[];
}

// ---------- Stocks Manage (admin/system/stocks-manage/*) ----------
//
// Shapes según los endpoints ya desplegados. Tipamos exclusivamente
// los campos que el frontend consume; cualquier campo extra que
// devuelva el backend queda accesible vía `any` en el caller.

export interface StocksSearchParams {
	query: string;
	withoutLoading?: boolean;
}

export interface StocksSearchMatch {
	symbol?: string;
	name?: string;
	type?: string;
	region?: string;
	currency?: string;
	matchScore?: number | string;
}

export interface StocksSearchResponse {
	query?: string;
	count?: number;
	matches?: StocksSearchMatch[];
}

export interface StocksAddParams {
	// Modo 1: lista explícita de símbolos + opcional mapa de nombres.
	symbols?: string[];
	names?: Record<string, string>;
	// Modo 2: pegar texto crudo (uno por línea o separados por comas).
	raw?: string;
	withoutLoading?: boolean;
}

export interface StocksAddedRow {
	symbol?: string;
	name?: string;
	price?: number | string;
	changePercent?: number | string;
}

export interface StocksAddFailed {
	symbol?: string;
	error?: string;
}

export interface StocksAddResponse {
	agregados?: StocksAddedRow[];
	duplicados?: (string | StocksAddedRow)[];
	agregados_sin_precio?: (string | StocksAddedRow)[];
	fallidos?: StocksAddFailed[];
	counts?: {
		agregados?: number;
		duplicados?: number;
		agregados_sin_precio?: number;
		fallidos?: number;
		[k: string]: number | undefined;
	};
}

export interface StaleStockRow {
	id?: number | string;
	symbol?: string;
	name?: string;
	paid_updated_at?: string | null;
	created_at?: string | null;
	price?: number | string | null;
}

export interface StocksStaleResponse {
	threshold_hours?: number;
	counts?: {
		pendientes?: number;
		muertas?: number;
	};
	pendientes?: StaleStockRow[];
	muertas?: StaleStockRow[];
}

export type DeadAction = 'retry' | 'delete';

export interface DeadActionParams {
	action: DeadAction;
	stock_id: number | string;
	withoutLoading?: boolean;
}

export interface DeadActionResponse {
	ok?: boolean;
	status?: 'actualizado' | 'sigue_sin_cotizar' | 'eliminado' | string;
	reason?: 'en_uso' | string;
	price?: number | string;
	changePercent?: number | string;
}

// ---------- Catálogo de stocks (list/users/edit) ----------

export type StocksListOrderBy = 'priority' | 'symbol' | 'name' | 'paid_updated_at';
export type StocksListOrderDir = 'asc' | 'desc';

export interface StocksListParams {
	page?: number;
	per_page?: number;
	search?: string;
	order_by?: StocksListOrderBy;
	order_dir?: StocksListOrderDir;
	withoutLoading?: boolean;
}

export interface StockRow {
	id?: number | string;
	symbol?: string;
	name?: string;
	price?: number | string | null;
	percent?: number | string | null;
	priority?: number | null;
	paid_updated_at?: string | null;
	color?: string | null;
	usuarios_count?: number;
}

export interface StocksListResponse {
	rows?: StockRow[];
	// `count` = número total de páginas según el spec ("count(páginas)").
	count?: number;
	total?: number;       // total de filas
	page?: number;
	per_page?: number;
}

export interface StockUsersParams {
	stock_id: number | string;
	withoutLoading?: boolean;
}

export interface StockUserRow {
	id?: number | string;
	name?: string;
	lastname?: string;
	email?: string;
}

export interface StockUsersResponse {
	stock_id?: number | string;
	count?: number;
	users?: StockUserRow[];
}

export interface EditStockParams {
	stock_id: number | string;
	name?: string;
	priority?: number;
	withoutLoading?: boolean;
}

export interface EditStockResponse {
	ok?: boolean;
	stock?: {
		id?: number | string;
		symbol?: string;
		name?: string;
		priority?: number;
	};
}

// ---------- Cryptos Manage (admin/system/cryptos-manage/*) ----------
//
// Espejo de stocks-manage con diferencias clave:
// - search: recibe `symbol` (exacto) y devuelve N matches con `id` (CMC id),
//   `rank`, `is_active`, `ya_existe`. El admin elige cuál agregar.
// - add: recibe `ids: number[]` (CMC ids), no símbolos.
// - list: `price` viene como STRING ya formateado, NO re-formatear como número.
// - dead-action y users usan `crypto_id` en lugar de `stock_id`.

export interface CryptosSearchParams {
	symbol: string;
	withoutLoading?: boolean;
}

export interface CryptosSearchMatch {
	id?: number;
	name?: string;
	symbol?: string;
	slug?: string;
	rank?: number | null;
	is_active?: boolean | number;
	ya_existe?: boolean;
}

export interface CryptosSearchResponse {
	symbol?: string;
	count?: number;
	matches?: CryptosSearchMatch[];
}

export interface CryptosAddParams {
	ids: number[];
	withoutLoading?: boolean;
}

export interface CryptosAddedRow {
	id?: number;
	symbol?: string;
	name?: string;
	price?: number | string;
}

export interface CryptosAddFailed {
	id?: number;
	symbol?: string;
	error?: string;
}

export interface CryptosAddResponse {
	agregados?: CryptosAddedRow[];
	duplicados?: (number | CryptosAddedRow)[];
	agregados_sin_precio?: (number | CryptosAddedRow)[];
	fallidos?: CryptosAddFailed[];
	counts?: Record<string, number | undefined>;
}

export interface CryptoStaleRow {
	id?: number | string;
	symbol?: string;
	name?: string;
	paid_updated_at?: string | null;
	created_at?: string | null;
	price?: string | number | null;
}

export interface CryptosStaleResponse {
	threshold_hours?: number;
	counts?: {
		pendientes?: number;
		muertas?: number;
	};
	pendientes?: CryptoStaleRow[];
	muertas?: CryptoStaleRow[];
}

export interface CryptoDeadActionParams {
	action: DeadAction;
	crypto_id: number | string;
	withoutLoading?: boolean;
}

// Misma shape que DeadActionResponse de stocks (ok/status/reason/...)
// — no la duplicamos.

export interface CryptosListParams {
	page?: number;
	per_page?: number;
	search?: string;
	order_by?: StocksListOrderBy;
	order_dir?: StocksListOrderDir;
	withoutLoading?: boolean;
}

export interface CryptoRow {
	id?: number | string;
	symbol?: string;
	name?: string;
	price?: string | number | null; // STRING ya formateado en la API
	percent?: number | string | null;
	priority?: number | null;
	paid_updated_at?: string | null;
	color?: string | null;
	usuarios_count?: number;
}

export interface CryptosListResponse {
	rows?: CryptoRow[];
	count?: number;
	total?: number;
	page?: number;
	per_page?: number;
}

export interface CryptoUsersParams {
	crypto_id: number | string;
	withoutLoading?: boolean;
}

// `users` igual que StockUsersResponse — no duplicamos shape.
export interface CryptoUsersResponse {
	crypto_id?: number | string;
	count?: number;
	users?: StockUserRow[];
}

export interface EditCryptoParams {
	crypto_id: number | string;
	name?: string;
	priority?: number;
	withoutLoading?: boolean;
}

export interface EditCryptoResponse {
	ok?: boolean;
	crypto?: {
		id?: number | string;
		symbol?: string;
		name?: string;
		priority?: number;
	};
}

// ---------- Threshold (umbral de stale, por asset) ----------

export interface SetThresholdParams {
	hours: number;
	withoutLoading?: boolean;
}

export interface SetThresholdResponse {
	ok?: boolean;
	hours?: number;
}

// ---------- Service ----------

class SystemService {

	static admin = {
		// CRON
		getCronStatus: async (params?: { withoutLoading?: boolean }) =>
			Api.createResponse('admin/system/cron/status', params) as Promise<CronStatusResponse>,

		getCronLogs: async (params?: CronLogsParams) =>
			Api.createResponse('admin/system/cron/logs', params) as Promise<CronLogsResponse>,

		updateCronConfig: async (params: { key_name: string; value: string }) =>
			Api.createResponse('admin/system/cron/config/update', params) as Promise<UpdateConfigResponse>,

		runCronNow: async (params: { job_name: string }) =>
			Api.createResponse('admin/system/cron/run-now', params) as Promise<RunNowResponse>,

		// SYSTEM
		getHealth: async (params?: { withoutLoading?: boolean }) =>
			Api.createResponse('admin/system/health', params) as Promise<HealthResponse>,

		getPm2Logs: async (params?: Pm2LogsParams) =>
			Api.createResponse('admin/system/logs/pm2', params) as Promise<Pm2LogsResponse>,

		getBackups: async (params?: { withoutLoading?: boolean }) =>
			Api.createResponse('admin/system/backups', params) as Promise<BackupsResponse>,

		// STOCKS MANAGE
		searchStocks: async (params: StocksSearchParams) =>
			Api.createResponse('admin/system/stocks-manage/search', params) as Promise<StocksSearchResponse>,

		addStocks: async (params: StocksAddParams) =>
			Api.createResponse('admin/system/stocks-manage/add', params) as Promise<StocksAddResponse>,

		getStaleStocks: async (params?: { withoutLoading?: boolean }) =>
			Api.createResponse('admin/system/stocks-manage/stale', params || {}) as Promise<StocksStaleResponse>,

		deadAction: async (params: DeadActionParams) =>
			Api.createResponse('admin/system/stocks-manage/dead-action', params) as Promise<DeadActionResponse>,

		// STOCKS CATÁLOGO
		listStocks: async (params?: StocksListParams) =>
			Api.createResponse('admin/system/stocks-manage/list', params || {}) as Promise<StocksListResponse>,

		stockUsers: async (params: StockUsersParams) =>
			Api.createResponse('admin/system/stocks-manage/users', params) as Promise<StockUsersResponse>,

		editStock: async (params: EditStockParams) =>
			Api.createResponse('admin/system/stocks-manage/edit', params) as Promise<EditStockResponse>,

		// CRYPTOS MANAGE (espejo de stocks-manage; ids CMC en vez de símbolos)
		searchCryptos: async (params: CryptosSearchParams) =>
			Api.createResponse('admin/system/cryptos-manage/search', params) as Promise<CryptosSearchResponse>,

		addCryptos: async (params: CryptosAddParams) =>
			Api.createResponse('admin/system/cryptos-manage/add', params) as Promise<CryptosAddResponse>,

		getStaleCryptos: async (params?: { withoutLoading?: boolean }) =>
			Api.createResponse('admin/system/cryptos-manage/stale', params || {}) as Promise<CryptosStaleResponse>,

		cryptoDeadAction: async (params: CryptoDeadActionParams) =>
			Api.createResponse('admin/system/cryptos-manage/dead-action', params) as Promise<DeadActionResponse>,

		listCryptos: async (params?: CryptosListParams) =>
			Api.createResponse('admin/system/cryptos-manage/list', params || {}) as Promise<CryptosListResponse>,

		cryptoUsers: async (params: CryptoUsersParams) =>
			Api.createResponse('admin/system/cryptos-manage/users', params) as Promise<CryptoUsersResponse>,

		editCrypto: async (params: EditCryptoParams) =>
			Api.createResponse('admin/system/cryptos-manage/edit', params) as Promise<EditCryptoResponse>,

		// THRESHOLDS — independientes por asset
		setStockThreshold: async (params: SetThresholdParams) =>
			Api.createResponse('admin/system/stocks-manage/set-threshold', params) as Promise<SetThresholdResponse>,

		setCryptoThreshold: async (params: SetThresholdParams) =>
			Api.createResponse('admin/system/cryptos-manage/set-threshold', params) as Promise<SetThresholdResponse>,

		// Download de backup: stream binario. Opción B del spec —
		// fetch con header Authorization, Blob, click sintético.
		// Usa axios (mismo cliente que el resto) en vez de fetch crudo
		// para respetar baseURL configurado y el patrón del codebase.
		downloadBackup: async (filename: string): Promise<void> => {
			const token = store.getState().access_token;
			const safe = encodeURIComponent(filename);
			const res: any = await axios.get(`admin/system/backups/download/${ safe }`, {
				responseType: 'blob',
				headers: { Authorization: `Bearer ${ token }` },
			});

			const blob: Blob = res.data;
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		},
	}
}

export default SystemService;
